import { canAddEntities } from './selectionLimit';
import {
  getEntitySelectionContext,
  isCompatibleContext,
  resolveContextForEntities,
} from './selectionContext';
import { EMPTY_SELECTION } from './selectionState';

const resolveCommitSource = (source, selection, nextKeysLength) => {
  if (!nextKeysLength) return null;
  return source ?? selection.source ?? null;
};

const buildSelectionFromEntries = (entries, {
  lastClickedKey = null,
  isComplete = false,
  context = null,
  source = null,
}) => {
  const byKey = {};
  const keys = [];

  entries.forEach((entry) => {
    keys.push(entry.key);
    byKey[entry.key] = entry;
  });

  return {
    keys,
    byKey,
    lastClickedKey,
    isComplete,
    context,
    source: keys.length ? source : null,
  };
};

const resolveHomogeneousContext = (entries) => {
  if (!entries.length) {
    return { ok: true, context: null };
  }

  const firstContext = getEntitySelectionContext(entries[0]);
  const conflictingEntry = entries.find((entry) => {
    const entryContext = getEntitySelectionContext(entry);
    return entryContext && firstContext && !isCompatibleContext(firstContext, entryContext);
  });

  if (conflictingEntry) {
    return {
      ok: false,
      context: firstContext,
      attemptedContext: getEntitySelectionContext(conflictingEntry),
    };
  }

  return { ok: true, context: firstContext ?? resolveContextForEntities(entries) };
};

/**
 * Applies a selection mutation atomically (limit + context). Returns the previous
 * selection unchanged when the operation cannot be committed.
 */
export default function commitSelection(selection, {
  addEntries = [],
  removeKeys = [],
  replaceEntries = null,
  lastClickedKey,
  context,
  source,
}) {
  if (replaceEntries !== null) {
    if (!replaceEntries.length) {
      return { selection: { ...EMPTY_SELECTION }, result: { ok: true } };
    }

    const limitCheck = canAddEntities(EMPTY_SELECTION, replaceEntries);
    if (!limitCheck.ok) {
      return { selection, result: limitCheck };
    }

    const homogeneous = resolveHomogeneousContext(replaceEntries);
    if (!homogeneous.ok) {
      return {
        selection,
        result: {
          ok: false,
          reason: 'incompatibleContext',
          currentContext: selection.context,
          attemptedContext: homogeneous.attemptedContext,
        },
      };
    }

    const nextContext = context ?? homogeneous.context;
    return {
      selection: buildSelectionFromEntries(replaceEntries, {
        lastClickedKey: lastClickedKey ?? replaceEntries[replaceEntries.length - 1]?.key ?? null,
        context: nextContext,
        source: resolveCommitSource(source, selection, replaceEntries.length),
      }),
      result: { ok: true },
    };
  }

  const removeSet = new Set(removeKeys);
  const hasRemovals = selection.keys.some((key) => removeSet.has(key));

  if (!addEntries.length) {
    if (!hasRemovals) {
      if (lastClickedKey !== undefined && lastClickedKey !== selection.lastClickedKey) {
        return {
          selection: {
            ...selection,
            lastClickedKey,
            isComplete: false,
          },
          result: { ok: true },
        };
      }
      return { selection, result: { ok: true } };
    }

    const byKey = { ...selection.byKey };
    removeKeys.forEach((key) => {
      delete byKey[key];
    });
    const keys = selection.keys.filter((key) => !removeSet.has(key));

    if (!keys.length) {
      return { selection: { ...EMPTY_SELECTION }, result: { ok: true } };
    }

    return {
      selection: {
        keys,
        byKey,
        lastClickedKey: removeSet.has(selection.lastClickedKey) ? null : selection.lastClickedKey,
        isComplete: false,
        context: selection.context,
        source: resolveCommitSource(source, selection, keys.length),
      },
      result: { ok: true },
    };
  }

  const entriesToAdd = addEntries.filter((entry) => !selection.byKey[entry.key]);
  const limitCheck = canAddEntities(selection, entriesToAdd);
  if (!limitCheck.ok) {
    return { selection, result: limitCheck };
  }

  const homogeneous = resolveHomogeneousContext(entriesToAdd);
  if (!homogeneous.ok) {
    return {
      selection,
      result: {
        ok: false,
        reason: 'incompatibleContext',
        currentContext: selection.keys.length ? selection.context : homogeneous.context,
        attemptedContext: homogeneous.attemptedContext,
      },
    };
  }

  const incomingContext = context ?? homogeneous.context;
  const activeContext = selection.keys.length ? selection.context : null;

  if (activeContext && incomingContext && !isCompatibleContext(activeContext, incomingContext)) {
    return {
      selection,
      result: {
        ok: false,
        reason: 'incompatibleContext',
        currentContext: activeContext,
        attemptedContext: incomingContext,
      },
    };
  }

  const byKey = { ...selection.byKey };
  const keys = [...selection.keys];

  entriesToAdd.forEach((entry) => {
    byKey[entry.key] = entry;
    keys.push(entry.key);
  });

  return {
    selection: {
      keys,
      byKey,
      lastClickedKey: lastClickedKey !== undefined ? lastClickedKey : selection.lastClickedKey,
      isComplete: false,
      context: activeContext || incomingContext,
      source: resolveCommitSource(source, selection, keys.length),
    },
    result: { ok: true },
  };
}
